/**
 * HeaderOverlay Component
 *
 * Renders the top-center header overlay with:
 * - "Mundus Novus" title (large bold text)
 * - Weather icon reflecting market sentiment
 * - Current date (small text)
 * - Market trend indicator (medium text)
 */

import React from 'react';
import {
  CARTOON_COLORS,
  WEATHER_ICONS,
  WEATHER_LABELS,
  MarketSentiment,
} from './cartoon.config';

interface HeaderOverlayProps {
  title?: string;
  mundusName?: string;
  sentiment: MarketSentiment;
  date: Date;
  className?: string;
}

export const HeaderOverlay: React.FC<HeaderOverlayProps> = ({
  title = 'Mundus Novus',
  mundusName,
  sentiment,
  date,
  className = '',
}) => {
  const weatherColors = CARTOON_COLORS.weather[sentiment.weather];
  const weatherIcon = WEATHER_ICONS[sentiment.weather];
  const weatherLabel = WEATHER_LABELS[sentiment.weather];

  const formattedDate = date.toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  const trendIcon = sentiment.direction === 'up' ? '↗' : sentiment.direction === 'down' ? '↘' : '→';
  const trendColor =
    sentiment.direction === 'up'
      ? '#34C759'
      : sentiment.direction === 'down'
        ? '#FF3B30'
        : '#8E8E93';

  return (
    <div
      className={`absolute top-0 left-0 right-0 z-10 pointer-events-none ${className}`}
      style={{ height: 140 }}
    >
      {/* Frosted glass background */}
      <div
        className="absolute inset-0"
        style={{
          background: 'linear-gradient(180deg, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0.85) 70%, rgba(255,255,255,0) 100%)',
          backdropFilter: 'blur(10px)',
        }}
      />

      {/* Content */}
      <div className="relative flex flex-col items-center justify-start pt-4 px-4">
        {/* Title */}
        <h1
          className="text-3xl font-bold tracking-tight"
          style={{
            color: CARTOON_COLORS.header.title,
            fontFamily: "'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
            textShadow: '0 1px 2px rgba(0,0,0,0.1)',
          }}
        >
          {title}
        </h1>

        {/* Mundus name subtitle */}
        {mundusName && (
          <p
            className="text-sm mt-0.5"
            style={{ color: CARTOON_COLORS.header.subtitle }}
          >
            {mundusName}
          </p>
        )}

        {/* Weather icon with sentiment */}
        <div
          className="flex items-center gap-2 mt-2 px-4 py-1.5 rounded-full"
          style={{
            backgroundColor: weatherColors.bg,
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          }}
        >
          <span className="text-2xl" role="img" aria-label={weatherLabel}>
            {weatherIcon}
          </span>
          <span
            className="text-sm font-medium"
            style={{ color: weatherColors.text }}
          >
            {weatherLabel} ({sentiment.score}%)
          </span>
        </div>

        {/* Date */}
        <p
          className="text-xs mt-2"
          style={{ color: CARTOON_COLORS.header.subtitle }}
        >
          {formattedDate}
        </p>

        {/* Market trend */}
        <div className="flex items-center gap-1.5 mt-1">
          <span
            className="text-lg font-semibold"
            style={{ color: trendColor }}
          >
            {trendIcon}
          </span>
          <span
            className="text-sm font-medium"
            style={{ color: trendColor }}
          >
            {sentiment.trend > 0 ? '+' : ''}{sentiment.trend.toFixed(1)}% trend
          </span>
        </div>
      </div>
    </div>
  );
};

/**
 * Draw header overlay directly on canvas (for pure canvas rendering)
 */
export function drawHeaderOverlay(
  ctx: CanvasRenderingContext2D,
  canvasWidth: number,
  title: string,
  mundusName: string | undefined,
  sentiment: MarketSentiment,
  date: Date
): void {
  const centerX = canvasWidth / 2;
  const weatherColors = CARTOON_COLORS.weather[sentiment.weather];
  const weatherIcon = WEATHER_ICONS[sentiment.weather];
  const weatherLabel = WEATHER_LABELS[sentiment.weather];

  // Frosted glass background gradient
  const bgGradient = ctx.createLinearGradient(0, 0, 0, 140);
  bgGradient.addColorStop(0, 'rgba(255, 255, 255, 0.95)');
  bgGradient.addColorStop(0.7, 'rgba(255, 255, 255, 0.88)');
  bgGradient.addColorStop(1, 'rgba(255, 255, 255, 0)');
  ctx.fillStyle = bgGradient;
  ctx.fillRect(0, 0, canvasWidth, 145);

  // Title
  ctx.fillStyle = CARTOON_COLORS.header.title;
  ctx.font = 'bold 32px "SF Pro Display", -apple-system, BlinkMacSystemFont, sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'top';

  // Text shadow for title
  ctx.save();
  ctx.shadowColor = 'rgba(0, 0, 0, 0.1)';
  ctx.shadowBlur = 2;
  ctx.shadowOffsetY = 1;
  ctx.fillText(title, centerX, 16);
  ctx.restore();

  // Mundus name subtitle
  let yOffset = 52;
  if (mundusName) {
    ctx.fillStyle = CARTOON_COLORS.header.subtitle;
    ctx.font = '14px "SF Pro Text", -apple-system, sans-serif';
    ctx.fillText(mundusName, centerX, yOffset);
    yOffset += 20;
  }

  // Weather badge background
  const badgeWidth = 160;
  const badgeHeight = 32;
  const badgeX = centerX - badgeWidth / 2;
  const badgeY = yOffset;

  ctx.fillStyle = weatherColors.bg;
  ctx.beginPath();
  ctx.roundRect(badgeX, badgeY, badgeWidth, badgeHeight, 16);
  ctx.fill();

  // Weather badge shadow
  ctx.save();
  ctx.shadowColor = 'rgba(0, 0, 0, 0.08)';
  ctx.shadowBlur = 8;
  ctx.shadowOffsetY = 2;
  ctx.fill();
  ctx.restore();

  // Weather icon
  ctx.font = '20px sans-serif';
  ctx.textBaseline = 'middle';
  ctx.fillText(weatherIcon, badgeX + 24, badgeY + badgeHeight / 2);

  // Weather label
  ctx.fillStyle = weatherColors.text;
  ctx.font = '500 13px "SF Pro Text", -apple-system, sans-serif';
  ctx.textAlign = 'left';
  ctx.fillText(`${weatherLabel} (${sentiment.score}%)`, badgeX + 48, badgeY + badgeHeight / 2);

  yOffset += badgeHeight + 8;

  // Date
  const formattedDate = date.toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
  ctx.fillStyle = CARTOON_COLORS.header.subtitle;
  ctx.font = '12px "SF Pro Text", -apple-system, sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'top';
  ctx.fillText(formattedDate, centerX, yOffset);

  yOffset += 18;

  // Market trend
  const trendIcon = sentiment.direction === 'up' ? '↗' : sentiment.direction === 'down' ? '↘' : '→';
  const trendColor =
    sentiment.direction === 'up'
      ? '#34C759'
      : sentiment.direction === 'down'
        ? '#FF3B30'
        : '#8E8E93';
  const trendText = `${trendIcon} ${sentiment.trend > 0 ? '+' : ''}${sentiment.trend.toFixed(1)}% trend`;

  ctx.fillStyle = trendColor;
  ctx.font = '600 14px "SF Pro Text", -apple-system, sans-serif';
  ctx.fillText(trendText, centerX, yOffset);
}

export default HeaderOverlay;
